import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  ChevronLeft, ChevronRight, Inbox, Search, ExternalLink, Eye, X,
  FileText, Loader2, Trash2, ArrowUpDown, ArrowUp, ArrowDown, Download, Mail,
} from 'lucide-react';

const ITEMS_PER_PAGE = 25;

const formatCurrency = (value) => {
  if (value == null) return '—';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const FacturasListado = () => {
  const { token, API_URL, user } = useAuth();
  const isSuperAdmin = user?.permisos === 6 || user?.username === 'admin';

  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalFacturas, setTotalFacturas] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [previewFactura, setPreviewFactura] = useState(null);
  const [previewPdfUrl, setPreviewPdfUrl] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [desde, setDesde] = useState('');
  const [hasta, setHasta] = useState('');
  const [orderBy, setOrderBy] = useState('nombre_archivo');
  const [orderDir, setOrderDir] = useState('DESC');

  const fetchFacturas = useCallback(async (p = 1, search = '', ob = orderBy, od = orderDir, d = desde, h = hasta) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: ITEMS_PER_PAGE });
      if (search) params.set('search', search);
      if (ob) params.set('order_by', ob);
      if (od) params.set('order_dir', od);
      if (d) params.set('desde', d);
      if (h) params.set('hasta', h);
      const res = await fetch(`${API_URL}/commercial/facturas?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al cargar facturas');
      const data = await res.json();
      setFacturas(data.facturas || data.data || []);
      const total = data.pagination?.total || data.total || 0;
      setTotalFacturas(total);
      setTotalPages(data.pagination?.totalPages || Math.ceil(total / ITEMS_PER_PAGE) || 1);
      setPage(p);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [API_URL, token, orderBy, orderDir, desde, hasta]);

  useEffect(() => {
    fetchFacturas(1);
  }, [fetchFacturas]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchFacturas(1, searchTerm, orderBy, orderDir, desde, hasta);
  };

  const handleSort = (column) => {
    const newDir = orderBy === column && orderDir === 'DESC' ? 'ASC' : 'DESC';
    setOrderBy(column);
    setOrderDir(newDir);
    fetchFacturas(1, searchTerm, column, newDir, desde, hasta);
  };

  const SortIcon = ({ column }) => {
    if (orderBy !== column) return <ArrowUpDown size={12} className="ml-1 opacity-40" />;
    return orderDir === 'ASC'
      ? <ArrowUp size={12} className="ml-1" />
      : <ArrowDown size={12} className="ml-1" />;
  };

  const handleExcelDownload = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.set('search', searchTerm);
      if (desde) params.set('desde', desde);
      if (hasta) params.set('hasta', hasta);
      const res = await fetch(`${API_URL}/commercial/facturas/excel?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Error al descargar Excel');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `facturas_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Error al descargar el archivo Excel');
    }
  };

  const handleDelete = async (factura) => {
    if (!window.confirm(`¿Eliminar factura ${factura.nombre_archivo}?\nSe eliminará de la base de datos y de la carpeta de pruebas.`)) return;
    setDeleting(factura.id);
    try {
      const res = await fetch(`${API_URL}/commercial/facturas/${factura.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        alert(err.message || 'Error al eliminar');
      } else {
        fetchFacturas(page, searchTerm, orderBy, orderDir, desde, hasta);
      }
    } catch (err) {
      alert('Error de conexión al eliminar');
    } finally {
      setDeleting(null);
    }
  };

  // Baja el PDF de la factura del backend (con auth) como blob. El backend lo
  // sirve por streaming desde el FTP: asi es el PDF real y no la landing de la
  // SPA (antes se apuntaba a comprassanitarias.com/facturas/*, que la SPA no sirve).
  const fetchFacturaBlobUrl = async (factura) => {
    const res = await fetch(`${API_URL}/commercial/facturas/${encodeURIComponent(factura.nombre_archivo)}/pdf`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) throw new Error('No se pudo abrir la factura');
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  };

  const openFacturaPdf = async (factura) => {
    try {
      const url = await fetchFacturaBlobUrl(factura);
      window.open(url, '_blank', 'noopener');
    } catch (e) {
      alert(e.message);
    }
  };

  // Enviar la factura por correo al cliente asignado. Si el cliente NO tiene
  // correo, el backend responde 422 con noEmail y avisamos.
  const handleSendEmail = async (factura) => {
    const cliente = factura.cliente_nombre || factura.id_cliente;
    if (!window.confirm(`¿Enviar la factura ${factura.num_factura || factura.nombre_archivo} por correo al cliente ${cliente}?`)) return;
    setSendingEmail(factura.id);
    try {
      const res = await fetch(`${API_URL}/commercial/facturas/${encodeURIComponent(factura.nombre_archivo)}/enviar`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 422 && data.noEmail) {
        alert(`⚠️ El cliente "${data.cliente || cliente}" NO tiene correo asignado. No se pudo enviar la factura.`);
      } else if (res.ok && data.sent) {
        alert(`✅ Factura enviada a: ${(data.destinatarios || []).join(', ')}`);
      } else {
        alert(data.message || 'No se pudo enviar la factura');
      }
    } catch (e) {
      alert('Error de conexión al enviar la factura');
    } finally {
      setSendingEmail(null);
    }
  };

  // Al abrir la vista previa, bajamos el PDF y lo mostramos en el iframe via blob.
  useEffect(() => {
    if (!previewFactura) return undefined;
    let objUrl = null;
    let cancelled = false;
    setPreviewPdfUrl(null);
    fetchFacturaBlobUrl(previewFactura)
      .then((url) => { if (cancelled) URL.revokeObjectURL(url); else { objUrl = url; setPreviewPdfUrl(url); } })
      .catch(() => {});
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); setPreviewPdfUrl(null); };
  }, [previewFactura]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
            <FileText size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Listado de Facturas</h1>
            <p className="text-sm text-gray-500">{totalFacturas} facturas registradas</p>
          </div>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex-shrink-0 px-4 py-3 border-b border-gray-100 bg-gray-50">
        <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[180px]">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por cliente o archivo..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <input
            type="date"
            value={desde}
            onChange={(e) => setDesde(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            title="Desde"
          />
          <input
            type="date"
            value={hasta}
            onChange={(e) => setHasta(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            title="Hasta"
          />
          <button type="submit" className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm rounded-lg transition-colors">
            Buscar
          </button>
          <button
            type="button"
            onClick={handleExcelDownload}
            className="flex items-center gap-1.5 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-lg transition-colors"
          >
            <Download size={15} />
            Descargar Excel
          </button>
        </form>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center h-40 gap-2 text-gray-400">
            <Loader2 size={20} className="animate-spin" />
            <span className="text-sm">Cargando...</span>
          </div>
        ) : facturas.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <Inbox size={36} />
            <span className="text-sm mt-2">No hay facturas registradas</span>
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-gray-50 z-10">
              <tr className="text-left text-gray-500 uppercase" style={{ fontSize: '10px' }}>
                <th className="px-2 py-2 cursor-pointer select-none hover:text-gray-700" onClick={() => handleSort('nombre_archivo')}>
                  <span className="inline-flex items-center">Factura<SortIcon column="nombre_archivo" /></span>
                </th>
                <th className="px-2 py-2">Cliente</th>
                <th className="px-2 py-2 cursor-pointer select-none hover:text-gray-700" onClick={() => handleSort('fecha')}>
                  <span className="inline-flex items-center">Fecha<SortIcon column="fecha" /></span>
                </th>
                <th className="px-2 py-2">Vto.</th>
                <th className="px-2 py-2 text-right">Base</th>
                <th className="px-2 py-2 text-right">Final</th>
                <th className="px-2 py-2 text-center w-10">PDF</th>
                <th className="px-2 py-2 text-center w-10"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {facturas.map((f) => (
                <tr key={f.id} className="hover:bg-gray-50">
                  <td className="px-2 py-1.5 font-medium">{f.num_factura || f.nombre_archivo}</td>
                  <td className="px-2 py-1.5 max-w-[180px] truncate" title={f.cliente_nombre || ''}>{f.cliente_nombre || f.id_cliente}</td>
                  <td className="px-2 py-1.5">{formatDate(f.fecha)}</td>
                  <td className="px-2 py-1.5">{formatDate(f.fecha_vencimiento)}</td>
                  <td className="px-2 py-1.5 text-right">{formatCurrency(f.base)}</td>
                  <td className="px-2 py-1.5 text-right">{formatCurrency(f.total || f.final)}</td>
                  <td className="px-2 py-1.5 text-center">
                    {f.nombre_archivo && (
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => setPreviewFactura(f)}
                          className="text-blue-500 hover:text-blue-700 transition-colors"
                          title="Ver PDF"
                        >
                          <Eye size={16} />
                        </button>
                        <button
                          onClick={() => handleSendEmail(f)}
                          disabled={sendingEmail === f.id}
                          className="text-gray-400 hover:text-green-600 transition-colors disabled:opacity-40"
                          title="Enviar factura por correo al cliente"
                        >
                          {sendingEmail === f.id ? <Loader2 size={15} className="animate-spin" /> : <Mail size={16} />}
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-2 py-1.5 text-center">
                    <button
                      onClick={() => handleDelete(f)}
                      disabled={deleting === f.id}
                      className="text-red-400 hover:text-red-600 transition-colors disabled:opacity-40"
                      title="Eliminar factura"
                    >
                      {deleting === f.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-white">
          <span className="text-sm text-gray-500">
            Página {page} de {totalPages}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => fetchFacturas(page - 1, searchTerm, orderBy, orderDir, desde, hasta)}
              disabled={page <= 1}
              className="p-1.5 rounded-md border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
            >
              <ChevronLeft size={16} />
            </button>
            <button
              onClick={() => fetchFacturas(page + 1, searchTerm, orderBy, orderDir, desde, hasta)}
              disabled={page >= totalPages}
              className="p-1.5 rounded-md border border-gray-300 disabled:opacity-40 hover:bg-gray-100"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* PDF Preview Modal */}
      {previewFactura && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setPreviewFactura(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-[90vw] h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            {/* Modal header */}
            <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
              <div>
                <h3 className="font-semibold text-gray-800">
                  Factura: {previewFactura.num_factura || previewFactura.nombre_archivo}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                  <FileText size={12} />
                  Ruta: /facturas/{previewFactura.nombre_archivo}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => openFacturaPdf(previewFactura)}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors"
                >
                  <ExternalLink size={14} />
                  Abrir en nueva pestaña
                </button>
                <button onClick={() => setPreviewFactura(null)} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors">
                  <X size={20} />
                </button>
              </div>
            </div>
            {/* PDF iframe */}
            <div className="flex-1 overflow-hidden">
              {previewPdfUrl ? (
                <iframe
                  src={previewPdfUrl}
                  className="w-full h-full border-0"
                  title={`Factura ${previewFactura.nombre_archivo}`}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-sm text-gray-400">
                  Cargando factura…
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FacturasListado;
